import * as React from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { ActionButton } from "@jhool-io/fe-components";
import { IAddendumDetails, NoteTypes, Source } from "../../types/notes.types";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import {
    INTAKE_SECTIONS,
    SOAP_NOTE_SECTIONS,
    TREATMENT_REVIEW_NOTE_SECTIONS,
} from "../../../../utils/constants";
import { cn, makeStringFirstLetterCapital } from "../../../../utils/helpers";

import ListState from "../../../../components/ListState/ListState";
import NoteContentItem from "../../../../components/Notes/NoteContent/NoteContentItem/NoteContentItem";
import ViewSupervisionNote from "../../../../components/Notes/NoteContent/ViewSupervisionNote/ViewSupervisionNote";
import ViewCancellationNote from "../../../../components/Notes/NoteContent/ViewCancellationNote/ViewCancellationNote";
import ViewRecordOfDisclosureNote from "../../../../components/Notes/NoteContent/ViewRecordOfDisclosureNote/ViewRecordOfDisclosureNote";
import ViewSoapNote from "../../../../components/Notes/NoteContent/ViewSoapNote/ViewSoapNote";
import ViewTerminationNote from "../../../../components/Notes/NoteContent/ViewTerminationNote/ViewTerminationNote";
import ViewSafetyPlanNote from "../../../../components/Notes/NoteContent/ViewSafetyPlanNote/ViewSafetyPlanNote";
import ViewTreatmentReviewNote from "../../../../components/Notes/NoteContent/ViewTreatmentReviewNote/ViewTreatementReviewNote";
import ViewIndividualIntakeNote from "../../../../components/Notes/NoteContent/ViewIndividualIntakeNote/ViewIndividualIntakeNote";
import ViewGeneralNote from "../../../../components/Notes/NoteContent/ViewGeneralNote/ViewGeneralNote";
import ViewFamilyIntakeNote from "../../../../components/Notes/NoteContent/ViewFamilyIntakeNote/ViewFamilyIntakeNote";
import ViewSupervisorCheckInNote from "../../../../components/Notes/NoteContent/ViewSupervisorCheckInNote/ViewSupervisorCheckInNote";
import DocumentViewer from "../../../../components/DocumentViewer/DocumentViewer";
import ViewPauseNotificationNote from "../../../../components/Notes/NoteContent/ViewPauseNotificationNote/ViewPauseNotificationNote";
import Loader from "../../../../shared-ui/Loader/Loader";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import ChevronLeftIcon from "../../../../icons/ChevronLeft";
import ViewStandardNoteTemplateType from "../../../../components/Notes/NoteContent/ViewStandardNoteTemplateType/ViewStandardNoteTemplateType";
import ViewPauseNotificationPsychNote from "../../../../components/Notes/NoteContent/ViewPauseNotificationPsychNote/ViewPauseNotificationPsychNote";
import ViewRecordOfDisclosurePsychNote from "../../../../components/Notes/NoteContent/ViewRecordOfDisclosurePsychNote/ViewRecordOfDisclosurePsychNote";
import ViewNoShowPsychNote from "../../../../components/Notes/NoteContent/ViewNoShowPsychNote/ViewNoShowPsychNote";
import ViewTerminationOfTreatment from "../../../../components/Notes/NoteContent/ViewTerminationOfTreatment/ViewTerminationOfTreatment";
import ViewInitialDiagnosticEvaluationNote from "../../../../components/Notes/NoteContent/ViewInitialDiagnosticEvaluationNote/ViewInitialDiagnosticEvaluationNote";
import ViewInitialSoapNote from "../../../../components/Notes/NoteContent/ViewInitialSoapNote/ViewInitialSoapNote";
import ViewSupervisorDiagnosticReview from "../../../../components/Notes/NoteContent/ViewSupervisorDiagnosticReview/ViewSupervisorDiagnosticReview";
import ViewFamilyInitialDiagnosticEvaluationNote from "../../../../components/Notes/NoteContent/ViewFamilyInitialDiagnosticEvaluationNote/ViewFamilyInitialDiagnosticEvaluationNote";
import ViewFamilyTreatmentReview from "../../../../components/Notes/NoteContent/ViewFamilyTreatmentReview/ViewFamilyTreatmentReview";
import ViewPsychosocialAssessment from "../../../../components/Notes/NoteContent/ViewPsychosocialAssessment/ViewPsychosocialAssessment";
import ViewFamilyPsychosocialAssessment from "../../../../components/Notes/NoteContent/ViewFamilyPsychosocialAssessment/ViewFamilyPsychosocialAssessment";
import ViewCommunicationWithClientNote from "../../../../components/Notes/NoteContent/ViewCommunicationWithClientNote/ViewCommunicationWithClientNote";
import ViewFormsAndDocumentationNote from "../../../../components/Notes/NoteContent/ViewFormsAndDocumentationNote/ViewFormsAndDocumentationNote";
import { AiSessionNoteReviewPanel } from "../AiSessionNoteReview/AiSessionNoteReview";
import { AiSessionNoteReviewBoundary } from "../../../../context/AiSessionNoteReviewContext";
import ViewConsultationMedNote from "../../../../components/Notes/NoteContent/ViewConsultationMedNote/ViewConsultationMedNote";
import ViewDocumentationRequestNoteMed from "../../../../components/Notes/NoteContent/ViewDocumentationRequestNoteMed/ViewDocumentationRequestNoteMed";
import ViewEvaluationSubmissionNoteMed from "../../../../components/Notes/NoteContent/ViewEvaluationSubmissionNoteMed/ViewEvaluationSubmissionNoteMed";
import ViewNoShowNoteMed from "../../../../components/Notes/NoteContent/ViewNoShowNoteMed/ViewNoShowNoteMed";
import ViewEvaluationManagementAndTherapyNoteMed from "../../../../components/Notes/NoteContent/ViewEvaluationManagementAndTherapyNoteMed/ViewEvaluationManagementAndTherapyNoteMed";
import ViewEvaluationManagementNoteMed from "../../../../components/Notes/NoteContent/ViewEvaluationManagementNoteMed/ViewEvaluationManagementNoteMed";
import ViewTermOfCareNoteMed from "../../../../components/Notes/NoteContent/ViewTermOfCareNoteMed/ViewTermOfCareNoteMed";
import ViewTreatmentReviewNoteMed from "../../../../components/Notes/NoteContent/ViewTreatmentReviewNoteMed/ViewTreatmentReviewNoteMed";
import ViewIntakeNoteMed from "../../../../components/Notes/NoteContent/ViewIntakeNoteMed/ViewIntakeNoteMed";

interface NoteContentProps {
    showSideNavigation?: boolean;
}

// Function to handle note contents to show
export const handleNoteContentToDisplay = (
    type: NoteTypes,
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    },
    fieldAddendums?: {
        [key: string]: IAddendumDetails[];
    } | null
) => {
    switch (type) {
        case NoteTypes.SUPERVISION_NOTE:
            return (
                <ViewSupervisionNote
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.CANCELLATION_NOTE:
            return (
                <ViewCancellationNote
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.RECORD_OF_DISCLOSURE:
            return (
                <ViewRecordOfDisclosureNote
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.FAMILY_SOAP_NOTE:
            return (
                <ViewSoapNote
                    noteContent={noteContent}
                    isFamilySoap
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.INDIVIDUAL_SOAP_NOTE:
            return (
                <ViewSoapNote
                    noteContent={noteContent}
                    isFamilySoap={false}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.TERMINATION_NOTE:
            return (
                <ViewTerminationNote
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.SAFETY_PLAN:
            return (
                <ViewSafetyPlanNote
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.INDIVIDUAL_TREATMENT_REVIEW:
            return (
                <ViewTreatmentReviewNote
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                    isFamilyTreatmentReview={false}
                />
            );
        case NoteTypes.FAMILY_TREATMENT_REVIEW:
            return (
                <ViewFamilyTreatmentReview
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.FAMILY_PSYCHOSOCIAL_ASSESSMENT:
            return (
                <ViewFamilyPsychosocialAssessment
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.PSYCHOSOCIAL_ASSESSMENT:
            return (
                <ViewPsychosocialAssessment
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.MINOR_PSYCHOSOCIAL_ASSESSMENT:
            return (
                <ViewPsychosocialAssessment
                    noteContent={noteContent}
                    fieldAddendums={fieldAddendums || null}
                    isMinor
                />
            );
        case NoteTypes.INDIVIDUAL_INTAKE_NOTE:
            return (
                <ViewIndividualIntakeNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                    isMinor={false}
                />
            );
        case NoteTypes.MINOR_INTAKE_NOTE:
            return (
                <ViewIndividualIntakeNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                    isMinor
                />
            );
        case NoteTypes.SUPERVISOR_CHECK_IN:
            return (
                <ViewSupervisorCheckInNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.FAMILY_INTAKE_NOTE:
            return (
                <ViewFamilyIntakeNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.GENERAL_UPDATE_NOTE:
            return (
                <ViewGeneralNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.PAUSE_NOTIFICATION_NOTE:
            return (
                <ViewPauseNotificationNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.TESTING_INTAKE_MINOR_INTAKE_PSYCH:
        case NoteTypes.INTEGRATING_PATIENT_DATA_PSYCH:
        case NoteTypes.ADMINISTRATION_PSYCH:
        case NoteTypes.ADMINISTRATION_TECH:
        case NoteTypes.SCORING_BY_TECH_PSYCH:
        case NoteTypes.SCORING_BY_PSYCH:
        case NoteTypes.INTERPRETATION_REPORT_WRITING_PSYCH:
        case NoteTypes.FEEDBACK_PSYCH:
            return (
                <ViewStandardNoteTemplateType
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.PAUSE_NOTIFICATION_PSYCH:
            return (
                <ViewPauseNotificationPsychNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.RECORD_OF_DISCLOSURE_PSYCH:
            return (
                <ViewRecordOfDisclosurePsychNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.NO_SHOW_PSYCH:
            return (
                <ViewNoShowPsychNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.TERMINATION_OF_TREATMENT_PSYCH:
            return (
                <ViewTerminationOfTreatment
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.INITIAL_DIAGNOSTIC_EVALUATION:
            return (
                <ViewInitialDiagnosticEvaluationNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.MINOR_INITIAL_DIAGNOSTIC_EVALUATION:
            return (
                <ViewInitialDiagnosticEvaluationNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                    isMinor
                />
            );
        case NoteTypes.FAMILY_INITIAL_DIAGNOSTIC_EVALUATION:
            return (
                <ViewFamilyInitialDiagnosticEvaluationNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.INITIAL_SOAP_NOTE:
            return (
                <ViewInitialSoapNote
                    noteContent={noteContent}
                    isFamilySoap={false}
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.FAMILY_INITIAL_SOAP_NOTE:
            return (
                <ViewInitialSoapNote
                    noteContent={noteContent}
                    isFamilySoap
                    fieldAddendums={fieldAddendums || null}
                />
            );
        case NoteTypes.SUPERVISOR_DIAGNOSTIC_REVIEW:
            return (
                <ViewSupervisorDiagnosticReview
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.COMMUNICATION_WITH_CLIENT:
            return (
                <ViewCommunicationWithClientNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.FORMS_AND_DOCUMENTATION_NOTE:
            return (
                <ViewFormsAndDocumentationNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.CONSULTATION_NOTE_MED:
            return (
                <ViewConsultationMedNote
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.DOCUMENTATION_REQUEST_NOTE_MED:
            return (
                <ViewDocumentationRequestNoteMed
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.EVALUATION_SUBMISSION_NOTE_MED:
            return (
                <ViewEvaluationSubmissionNoteMed
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.NO_SHOW_NOTE_MED:
            return (
                <ViewNoShowNoteMed
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED:
            return (
                <ViewEvaluationManagementAndTherapyNoteMed
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.EVALUATION_MANAGEMENT_NOTE_MED:
            return (
                <ViewEvaluationManagementNoteMed
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.TERM_OF_CARE_NOTE_MED:
            return (
                <ViewTermOfCareNoteMed
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.TREATMENT_REVIEW_NOTE_MED:
            return (
                <ViewTreatmentReviewNoteMed
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        case NoteTypes.INTAKE_NOTE_MED:
            return (
                <ViewIntakeNoteMed
                    fieldAddendums={fieldAddendums || null}
                    noteContent={noteContent}
                />
            );
        default:
            return null;
    }
};

function NoteContentBody({ showSideNavigation = true }: NoteContentProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const location = useLocation();

    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const sectionsNav = searchParams.get("sections");

    const { data, error, isLoading, isSuccess } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    // Toggle note sections sidenav for long notes
    const handleToggleSectionsNav = () => {
        if (sectionsNav) {
            searchParams.delete("sections");
            setSearchParams(searchParams);
        } else {
            searchParams.set("sections", "open");
            setSearchParams(searchParams);
        }
    };

    const getSectionsNavLinks = (sectionItems: { [key: string]: string }) => {
        return Object.keys(sectionItems).map((item) => {
            return {
                title: item,
                url: `#${sectionItems[item as keyof typeof sectionItems]}`,
            };
        });
    };

    const getSectionNavLinksToDisplay = (noteType: NoteTypes) => {
        switch (noteType) {
            case NoteTypes.INDIVIDUAL_INTAKE_NOTE:
            case NoteTypes.FAMILY_INTAKE_NOTE:
            case NoteTypes.MINOR_INTAKE_NOTE:
            case NoteTypes.INITIAL_DIAGNOSTIC_EVALUATION:
            case NoteTypes.MINOR_INITIAL_DIAGNOSTIC_EVALUATION:
            case NoteTypes.FAMILY_INITIAL_DIAGNOSTIC_EVALUATION:
                return getSectionsNavLinks(INTAKE_SECTIONS);

            case NoteTypes.INDIVIDUAL_TREATMENT_REVIEW:
            case NoteTypes.FAMILY_TREATMENT_REVIEW:
            case NoteTypes.PSYCHOSOCIAL_ASSESSMENT:
            case NoteTypes.FAMILY_PSYCHOSOCIAL_ASSESSMENT:
            case NoteTypes.MINOR_PSYCHOSOCIAL_ASSESSMENT:
                return getSectionsNavLinks(TREATMENT_REVIEW_NOTE_SECTIONS);

            case NoteTypes.FAMILY_SOAP_NOTE:
            case NoteTypes.INDIVIDUAL_SOAP_NOTE:
            case NoteTypes.FAMILY_INITIAL_SOAP_NOTE:
                return getSectionsNavLinks(SOAP_NOTE_SECTIONS);

            default:
                return [];
        }
    };

    const showSectionsNavigation =
        [
            NoteTypes.FAMILY_INTAKE_NOTE,
            NoteTypes.INDIVIDUAL_INTAKE_NOTE,
            NoteTypes.MINOR_INTAKE_NOTE,
            NoteTypes.INDIVIDUAL_TREATMENT_REVIEW,
            NoteTypes.FAMILY_TREATMENT_REVIEW,
            NoteTypes.FAMILY_SOAP_NOTE,
            NoteTypes.INDIVIDUAL_SOAP_NOTE,
            NoteTypes.PSYCHOSOCIAL_ASSESSMENT,
            NoteTypes.INITIAL_DIAGNOSTIC_EVALUATION,
            NoteTypes.MINOR_INITIAL_DIAGNOSTIC_EVALUATION,
            NoteTypes.FAMILY_INITIAL_DIAGNOSTIC_EVALUATION,
            NoteTypes.FAMILY_INITIAL_SOAP_NOTE,
            NoteTypes.FAMILY_PSYCHOSOCIAL_ASSESSMENT,
            NoteTypes.MINOR_PSYCHOSOCIAL_ASSESSMENT,
        ].includes(data?.data?.type as NoteTypes) && showSideNavigation;

    React.useEffect(() => {
        const addendumSection = location.hash.split("?")[0];

        if (
            addendumSection &&
            addendumSection.includes("#") &&
            document.querySelector(`article${addendumSection}`)
        ) {
            const scrollToId = document.querySelector(
                `article${addendumSection}`
            ) as HTMLElement;

            scrollToId.scrollIntoView({ behavior: "instant" });
        }
    }, [location.hash]);

    return (
        <div>
            {isLoading && <Loader wrapperClassName="mt-[--spacing-header]" />}
            {error && (
                <div className="mt-[--spacing-header]">
                    <ListState
                        isError
                        errorMsg={
                            error.response?.data.message ||
                            "Cannot display note content at this time"
                        }
                        stateHelperText="Try reloading this page to solve this issue"
                    />
                </div>
            )}
            {data?.data &&
                isSuccess &&
                (data.data.source === Source.MANUAL ? (
                    <DocumentViewer
                        note={data.data}
                        file={data.data.note_url}
                    />
                ) : (
                    <div className="flex flex-col gap-y-[--spacing-items] sm:flex-row sm:items-start mt-[--spacing-header] relative">
                        {showSectionsNavigation ? (
                            <div
                                className={cn(
                                    "w-full sm:w-auto bg-transparent pb-5 sm:pb-0 sm:mr-8 border-r-0 sm:[border-right:1px_solid_var(--color-stroke)] hidden sticky left-0 will-change-transform top-[20px] pr-0 sm:pr-5",
                                    {
                                        block: sectionsNav === "open",
                                    }
                                )}
                            >
                                <div className="flex items-center gap-x-2 mb-[--spacing-items]">
                                    <ActionButton
                                        type="button"
                                        variant="can-hover"
                                        size="auto"
                                        className="shrink-0 capitalize bg-[--card-bg-dark] px-2 shadow-light_shadow flex items-center justify-center !rounded-full"
                                        onClick={handleToggleSectionsNav}
                                        aria-label="Close note sections navigation"
                                        actionTitle="Close sections naviagtion"
                                    >
                                        <ChevronLeftIcon />
                                    </ActionButton>
                                    <span className="font-medium text-base">
                                        Sections
                                    </span>
                                </div>
                                <ul className="flex flex-wrap gap-[--spacing-items] sm:flex-col">
                                    {getSectionNavLinksToDisplay(
                                        data.data.type
                                    ).map((link) => (
                                        <li
                                            key={link.title}
                                            className="text-textSec text-caption font-medium pr-4 pl-20"
                                        >
                                            <a
                                                href={link.url}
                                                className={cn(
                                                    "[border-bottom:2px_solid_transparent]",
                                                    {
                                                        "text-text [border-bottom:2px_solid_var(--color-text-link)]":
                                                            location.hash.includes(
                                                                link.url
                                                            ),
                                                    }
                                                )}
                                            >
                                                {makeStringFirstLetterCapital(
                                                    link.title
                                                )}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                        <div className="flex-1 bg-[--card-bg] px-[--padding-card-y] py-[--padding-card-y] rounded-md">
                            {showSideNavigation ? (
                                <AiSessionNoteReviewPanel />
                            ) : null}
                            {showSectionsNavigation ? (
                                <div
                                    className={cn("mb-[--spacing-items]", {
                                        hidden: sectionsNav === "open",
                                    })}
                                >
                                    <ActionButton
                                        variant="normal"
                                        size="auto"
                                        onClick={handleToggleSectionsNav}
                                        aria-label="Open note sections navigation"
                                        className="text-textLink text-base font-medium underline gap-x-2"
                                        actionTitle="Open sections naviagtion"
                                    >
                                        Sections
                                        <span
                                            aria-hidden="true"
                                            className="flex items-center justify-center size-6 rounded-full bg-[--card-bg-gray] capitalize shadow-light_shadow"
                                        >
                                            <ChevronRightIcon />
                                        </span>
                                    </ActionButton>
                                </div>
                            ) : null}
                            <h2 className="text-text text-base font-bold mb-[--spacing-header]">
                                NOTE CONTENT
                            </h2>
                            <div className="flex flex-col gap-y-2">
                                {handleNoteContentToDisplay(
                                    data.data.type,
                                    data.data.note_content,
                                    data.data.note_addendums.field_addendums
                                )}
                            </div>
                            {data.data.note_addendums?.general_addendums &&
                            data.data.note_addendums?.general_addendums.length >
                                0 ? (
                                <div style={{ marginTop: "8px" }}>
                                    <NoteContentItem
                                        headerText="NOTES ADDENDUM"
                                        addendums={
                                            data.data.note_addendums
                                                .general_addendums
                                        }
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>
                ))}
        </div>
    );
}

export default function NoteContent({
    showSideNavigation = true,
}: NoteContentProps) {
    const { noteId } = useParams();
    return (
        <AiSessionNoteReviewBoundary noteId={noteId as string}>
            <NoteContentBody showSideNavigation={showSideNavigation} />
        </AiSessionNoteReviewBoundary>
    );
}
